"use client";

/**
 * BadkamersBlock — "Laat u inspireren" badkamer-stijlen galerij met 4 layout-varianten.
 *
 * Port van Claude Design "Badkamers Blok.html":
 *   - grid       : clean 3×2 raster, alle tiles gelijk
 *   - mozaiek    : editorial — 1 hero links + 5 kleinere tiles
 *   - dark       : premium dark galerij met gouden accenten, 3×2 portrait
 *   - bento      : asymmetrisch bento-grid met mixed tile-groottes
 *
 * Zes ingebouwde stijl-illustraties (modern, landelijk, industrieel,
 * scandinavisch, klassiek, spa). Admin kan per stijl eigen foto meegeven
 * via image_src; anders valt het terug op de SVG placeholder.
 */

import { type CSSProperties } from "react";
import NextImage from "next/image";

export type BadkamersVariant = "grid" | "mozaiek" | "dark" | "bento";

export type BadkamerStyleId =
  | "modern"
  | "landelijk"
  | "industrieel"
  | "scandinavisch"
  | "klassiek"
  | "spa";

export interface BadkamerStyle {
  id: string;
  name: string;
  /** Welke ingebouwde illustratie gebruikt moet worden (valt terug op 'modern'). */
  illustration?: BadkamerStyleId;
  /** Eigen foto — overstemt de illustratie wanneer gezet. */
  image_src?: string;
  /** Expliciete alt-tekst — overstemt de auto-gegenereerde alt op basis van name+city. */
  image_alt?: string;
}

export interface BadkamersContent {
  eyebrow?: string;
  headline?: string;
  headline_italic?: string;
  subtitle?: string;
  filter_label?: string;
  styles?: BadkamerStyle[];
  /** Wordt door applyCityToBlock geïnjecteerd — voor stadsspecifieke alt-texts */
  city?: string;
}

interface Props {
  content: BadkamersContent;
  variant?: BadkamersVariant;
}

const T = {
  cream: "var(--cream, #EAE4D7)",
  creamSoft: "var(--cream-dark, #F4EFE1)",
  ink: "var(--ink, #1C1C1A)",
  inkSoft: "#3A3A36",
  muted: "var(--stone, #6E6A60)",
  line: "#D8D1BE",
  cta: "var(--terracotta, #FF6B1A)",
  white: "#FDFBF5",
  gold: "#C9A25F",
  dark: "#14130F",
  darkSoft: "#1F1D18",
  darkLine: "#33302A",
};

// ── Stijl-illustraties (SVG placeholders — vervangbaar via image_src) ──────

type Palette = {
  bg?: string;
  wall?: string;
  floor?: string;
  fixture?: string;
  accent?: string;
  green?: string;
  line?: string;
};

function StylePhoto({ id, dark = false }: { id: string; dark?: boolean }) {
  const palette: Palette = dark
    ? {
        bg: "#2A2721",
        wall: "#211E19",
        floor: "#1A1813",
        fixture: "#D8CFB8",
        accent: "#C9A25F",
        green: "#4A5C3D",
        line: "#3A362E",
      }
    : {};
  const render = photoFor(id);
  return (
    <svg
      viewBox="0 0 400 300"
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid slice"
      style={{ display: "block" }}
    >
      {render(palette, dark)}
    </svg>
  );
}

function photoFor(id: string): (p: Palette, dark: boolean) => React.ReactNode {
  const photos: Record<string, (p: Palette, dark: boolean) => React.ReactNode> = {
    modern: ({ bg, wall, floor, fixture, accent, line }, dark) => (
      <>
        <rect width="400" height="300" fill={bg || "#EEE8DC"} />
        <rect x="0" y="0" width="400" height="230" fill={wall || "#E2DAC7"} />
        <rect x="0" y="230" width="400" height="70" fill={floor || "#C9BFA6"} />
        <rect x="20" y="20" width="140" height="180" fill={dark ? "#3A3830" : "#F6F2E6"} stroke={line || "#CFC8B4"} />
        <line x1="90" y1="20" x2="90" y2="200" stroke={line || "#CFC8B4"} strokeWidth="0.6" />
        <line x1="20" y1="110" x2="160" y2="110" stroke={line || "#CFC8B4"} strokeWidth="0.6" />
        <path
          d="M180 180 Q180 175 186 175 L360 175 Q366 175 366 180 L360 220 Q360 236 344 236 L202 236 Q186 236 186 220 Z"
          fill={fixture || "#FDFBF5"}
          stroke={line || "#CFC8B4"}
        />
        <rect x="188" y="150" width="3" height="25" fill={accent || "#2E2C28"} />
        <rect x="186" y="146" width="14" height="6" fill={accent || "#2E2C28"} />
        <rect x="340" y="230" width="20" height="36" fill={dark ? "#211E19" : "#D8D1BE"} />
        <path d="M340 230 Q336 212 350 208 Q362 218 360 230 Z" fill={dark ? "#4A5C3D" : "#7A8E6F"} />
      </>
    ),
    landelijk: ({ bg, wall, floor, fixture, accent, line }, dark) => (
      <>
        <rect width="400" height="300" fill={bg || "#EFE6D2"} />
        <rect x="0" y="0" width="400" height="220" fill={wall || "#E8DEC4"} />
        {Array.from({ length: 14 }).map((_, i) => (
          <line
            key={i}
            x1={i * 30}
            y1="0"
            x2={i * 30}
            y2="220"
            stroke={dark ? "#2A2721" : "#D4C8AA"}
            strokeWidth="0.6"
          />
        ))}
        <rect x="0" y="220" width="400" height="80" fill={floor || "#B8A77F"} />
        <path
          d="M60 170 Q60 164 68 164 L320 164 Q328 164 328 170 L320 220 Q320 240 300 240 L88 240 Q68 240 68 220 Z"
          fill={fixture || "#FDFBF5"}
          stroke={line || "#B8A77F"}
        />
        <circle cx="80" cy="250" r="8" fill={accent || "#8C6D42"} />
        <circle cx="308" cy="250" r="8" fill={accent || "#8C6D42"} />
        <rect x="280" y="30" width="90" height="90" fill={dark ? "#3A3830" : "#F6F2E6"} stroke={accent || "#8C6D42"} strokeWidth="2" />
        <line x1="325" y1="30" x2="325" y2="120" stroke={accent || "#8C6D42"} strokeWidth="1.5" />
        <line x1="280" y1="75" x2="370" y2="75" stroke={accent || "#8C6D42"} strokeWidth="1.5" />
      </>
    ),
    industrieel: ({ bg, wall, floor, accent }, dark) => (
      <>
        <rect width="400" height="300" fill={bg || "#B5AFA2"} />
        <rect x="0" y="0" width="400" height="230" fill={wall || "#9F9A8D"} />
        {Array.from({ length: 8 }).map((_, i) => (
          <rect key={i} x={(i * 47) % 380} y={(i * 29) % 200} width="2" height="2" fill="#000" opacity="0.15" />
        ))}
        <rect x="0" y="230" width="400" height="70" fill={floor || "#6A6557"} />
        <rect x="40" y="40" width="160" height="220" fill="none" stroke={dark ? "#000" : "#1C1C1A"} strokeWidth="3" />
        <line x1="120" y1="40" x2="120" y2="260" stroke={dark ? "#000" : "#1C1C1A"} strokeWidth="2" />
        <line x1="40" y1="150" x2="200" y2="150" stroke={dark ? "#000" : "#1C1C1A"} strokeWidth="2" />
        <rect x="40" y="40" width="160" height="220" fill={dark ? "#14130F" : "#D8D1BE"} opacity="0.25" />
        <line x1="160" y1="40" x2="160" y2="90" stroke="#1C1C1A" strokeWidth="3" />
        <rect x="148" y="88" width="24" height="6" fill="#1C1C1A" />
        <rect x="260" y="40" width="4" height="180" fill={accent || "#8A7B5F"} />
        <rect x="240" y="220" width="44" height="10" fill={accent || "#8A7B5F"} />
        <circle cx="320" cy="60" r="12" fill={dark ? "#F4D279" : "#F5D97A"} opacity="0.85" />
        <line x1="320" y1="0" x2="320" y2="48" stroke="#1C1C1A" strokeWidth="1" />
      </>
    ),
    scandinavisch: ({ bg, wall, floor, fixture, accent, line }, dark) => (
      <>
        <rect width="400" height="300" fill={bg || "#F3EDDF"} />
        <rect x="0" y="0" width="400" height="220" fill={wall || "#EFE8D6"} />
        <rect x="0" y="220" width="400" height="80" fill={floor || "#D4B88A"} />
        {Array.from({ length: 6 }).map((_, i) => (
          <line
            key={i}
            x1={i * 70}
            y1="220"
            x2={i * 70}
            y2="300"
            stroke={dark ? "#A0865E" : "#B89864"}
            strokeWidth="0.8"
            opacity="0.6"
          />
        ))}
        <circle cx="110" cy="90" r="48" fill={dark ? "#3A3830" : "#F6F2E6"} stroke={line || "#C8BB9A"} strokeWidth="1.5" />
        <rect x="40" y="170" width="140" height="70" fill={dark ? "#3E3224" : "#C8A878"} stroke={dark ? "#2A2721" : "#A88858"} strokeWidth="1" />
        <rect x="58" y="160" width="104" height="14" rx="2" fill={fixture || "#FDFBF5"} />
        <rect x="108" y="142" width="4" height="18" fill={dark ? "#FDFBF5" : "#1C1C1A"} />
        <circle cx="340" cy="200" r="20" fill={accent || "#7A8E6F"} />
        <rect x="330" y="200" width="20" height="30" fill={dark ? "#2A2721" : "#A88858"} />
        <line x1="340" y1="180" x2="340" y2="170" stroke={accent || "#7A8E6F"} strokeWidth="2" />
        <line x1="350" y1="185" x2="360" y2="175" stroke={accent || "#7A8E6F"} strokeWidth="2" />
      </>
    ),
    klassiek: ({ bg, wall, floor, fixture, accent, line }, dark) => (
      <>
        <rect width="400" height="300" fill={bg || "#E8DCC4"} />
        <rect x="0" y="0" width="400" height="220" fill={wall || "#E2D6BE"} />
        <path d="M0 40 Q100 30 200 50 T400 45 L400 60 Q300 70 200 60 T0 55 Z" fill={dark ? "#2A2721" : "#D0C2A4"} opacity="0.4" />
        <path d="M0 130 Q120 120 220 140 T400 135 L400 150 Q280 160 180 145 T0 150 Z" fill={dark ? "#2A2721" : "#D0C2A4"} opacity="0.3" />
        <rect x="0" y="220" width="400" height="80" fill={floor || "#F6F2E6"} />
        {Array.from({ length: 8 }).map((_, i) =>
          Array.from({ length: 2 }).map((__, j) =>
            (i + j) % 2 === 0 ? (
              <rect
                key={`${i}-${j}`}
                x={i * 50}
                y={220 + j * 40}
                width="50"
                height="40"
                fill={dark ? "#211E19" : "#6E6A60"}
                opacity={dark ? 1 : 0.8}
              />
            ) : null
          )
        )}
        <rect x="160" y="130" width="80" height="40" rx="4" fill={fixture || "#FDFBF5"} stroke={line || "#A08C68"} />
        <path d="M186 170 L214 170 L210 230 L190 230 Z" fill={fixture || "#FDFBF5"} stroke={line || "#A08C68"} />
        <rect x="170" y="30" width="60" height="90" rx="30" fill={dark ? "#3A3830" : "#F6F2E6"} stroke={accent || "#A88858"} strokeWidth="3" />
        <circle cx="200" cy="22" r="6" fill="none" stroke={accent || "#A88858"} strokeWidth="2" />
        <circle cx="100" cy="80" r="10" fill={dark ? "#F4D279" : "#F5D97A"} opacity="0.85" />
        <circle cx="300" cy="80" r="10" fill={dark ? "#F4D279" : "#F5D97A"} opacity="0.85" />
      </>
    ),
    spa: ({ bg, floor, fixture, accent }, dark) => (
      <>
        <rect width="400" height="300" fill={bg || "#DDD3BE"} />
        <defs>
          <linearGradient id="spaWallBk" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={dark ? "#2A2721" : "#E5D9BB"} />
            <stop offset="1" stopColor={dark ? "#211E19" : "#CFC1A0"} />
          </linearGradient>
        </defs>
        <rect x="0" y="0" width="400" height="220" fill="url(#spaWallBk)" />
        <rect x="0" y="220" width="400" height="80" fill={floor || "#8B7D5E"} />
        <ellipse cx="200" cy="230" rx="130" ry="22" fill={dark ? "#14130F" : "#B89E6F"} />
        <ellipse cx="200" cy="226" rx="120" ry="18" fill={dark ? "#3A3830" : "#E8DDBE"} />
        {[140, 180, 220, 260].map((x, i) => (
          <g key={i}>
            <ellipse cx={x} cy="225" rx="8" ry="3" fill={dark ? "#1C1C1A" : "#8B7D5E"} />
            <rect x={x - 6} y="218" width="12" height="8" fill={fixture || "#FDFBF5"} />
            <ellipse cx={x} cy="213" rx="3" ry="5" fill={accent || "#F5A04A"} opacity="0.9" />
          </g>
        ))}
        <line x1="320" y1="0" x2="320" y2="70" stroke={dark ? "#4A4439" : "#8A7B5F"} strokeWidth="1" />
        <ellipse cx="320" cy="78" rx="14" ry="10" fill={dark ? "#F4D279" : "#F5D97A"} opacity="0.85" />
        <rect x="40" y="170" width="60" height="10" fill={dark ? "#D8CFB8" : "#FDFBF5"} rx="2" />
        <rect x="40" y="160" width="60" height="10" fill={dark ? "#C9A25F" : "#D4B88A"} rx="2" />
        <rect x="40" y="150" width="60" height="10" fill={dark ? "#D8CFB8" : "#FDFBF5"} rx="2" />
      </>
    ),
  };
  return photos[id] ?? photos.modern!;
}

function StyleMedia({ style, dark = false, city }: { style: BadkamerStyle; dark?: boolean; city?: string }) {
  if (style.image_src) {
    const altBase = `Badkamerstijl ${style.name}`;
    const autoAlt = city ? `${altBase} — voorbeeld renovatie ${city}` : altBase;
    const alt = style.image_alt
      ? (city
          ? style.image_alt.replace(/\{\{\s*city\s*\}\}/gi, city)
          : style.image_alt.replace(/\s*\{\{\s*city\s*\}\}\s*/gi, " ").trim())
      : autoAlt;
    return (
      <NextImage
        src={style.image_src}
        alt={alt}
        fill
        loading="lazy"
        sizes="(max-width: 700px) 100vw, (max-width: 1100px) 50vw, 33vw"
        quality={78}
        style={{ objectFit: "cover", display: "block" }}
      />
    );
  }
  return <StylePhoto id={style.illustration ?? style.id} dark={dark} />;
}

// ── Defaults ──────────────────────────────────────────────────────────────
const DEFAULT_STYLES: BadkamerStyle[] = [
  { id: "modern", name: "Modern", illustration: "modern" },
  { id: "landelijk", name: "Landelijk", illustration: "landelijk" },
  { id: "industrieel", name: "Industrieel", illustration: "industrieel" },
  { id: "scandinavisch", name: "Scandinavisch", illustration: "scandinavisch" },
  { id: "klassiek", name: "Klassiek", illustration: "klassiek" },
  { id: "spa", name: "Spa & Wellness", illustration: "spa" },
];

function resolveStyles(raw: BadkamerStyle[] | undefined): BadkamerStyle[] {
  if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_STYLES;
  return raw.filter((s) => s && typeof s.name === "string" && s.name.length > 0);
}


// ── Section header ────────────────────────────────────────────────────────
function Header({
  theme = "light",
  eyebrow,
  headline,
  headlineItalic,
  subtitle,
  align = "left",
}: {
  theme?: "light" | "dark";
  eyebrow?: string | undefined;
  headline?: string | undefined;
  headlineItalic?: string | undefined;
  subtitle?: string | undefined;
  align?: "left" | "center";
}) {
  const dark = theme === "dark";
  return (
    <div
      style={{
        textAlign: align,
        ...(align === "center" ? { marginLeft: "auto", marginRight: "auto" } : {}),
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.18em",
          color: dark ? T.gold : T.cta,
          marginBottom: 10,
          textTransform: "uppercase",
        }}
      >
        {eyebrow ?? "Laat u inspireren"}
      </div>
      <h2
        style={{
          fontFamily: "'Fraunces', serif",
          fontWeight: 500,
          fontSize: "clamp(28px, 3.6vw, 44px)",
          lineHeight: 1.08,
          color: dark ? "#F6F0DE" : T.ink,
          margin: 0,
          letterSpacing: "-0.02em",
          maxWidth: 680,
          ...(align === "center" ? { marginLeft: "auto", marginRight: "auto" } : {}),
        }}
      >
        {headline ? (
          <>
            {headline}
            {headlineItalic ? (
              <>
                {" "}
                <span style={{ fontStyle: "italic", fontWeight: 400 }}>{headlineItalic}</span>
              </>
            ) : null}
          </>
        ) : (
          <>
            Zes stijlen, <span style={{ fontStyle: "italic", fontWeight: 400 }}>één die bij u past</span>
          </>
        )}
      </h2>
      {subtitle ? (
        <div
          style={{
            fontSize: 15,
            color: dark ? "#B9B29D" : T.inkSoft,
            lineHeight: 1.6,
            marginTop: 12,
            maxWidth: 520,
            ...(align === "center" ? { marginLeft: "auto", marginRight: "auto" } : {}),
          }}
        >
          {subtitle}
        </div>
      ) : null}
    </div>
  );
}

// ── Tile ──────────────────────────────────────────────────────────────────
function StyleTile({
  s,
  aspect = "4 / 3",
  dark = false,
  size = "md",
  city,
}: {
  s: BadkamerStyle;
  aspect?: string;
  dark?: boolean;
  size?: "sm" | "md" | "lg";
  city?: string;
}) {
  const captionSize = size === "lg" ? 26 : size === "sm" ? 15 : 19;
  return (
    <a
      href="#"
      style={{
        display: "block",
        textDecoration: "none",
        color: "inherit",
        borderRadius: 14,
        overflow: "hidden",
        background: dark ? T.darkSoft : T.white,
        border: `1px solid ${dark ? T.darkLine : T.line}`,
        transition: "transform 0.25s, box-shadow 0.25s",
        height: "100%",
      }}
    >
      <div style={{ aspectRatio: aspect, width: "100%", position: "relative" }}>
        <StyleMedia style={s} dark={dark} {...(city ? { city } : {})} />
      </div>
      <div style={{ padding: "16px 20px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <h3
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: captionSize,
            fontWeight: 500,
            color: dark ? "#F6F0DE" : T.ink,
            margin: 0,
            letterSpacing: "-0.01em",
          }}
        >
          {s.name}
        </h3>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: T.cta }} />
      </div>
    </a>
  );
}

// ── Variant 1 — Clean 3×2 grid ───────────────────────────────────────────
function GridVariant({ content }: { content: BadkamersContent }) {
  const styles = resolveStyles(content.styles);
  return (
    <section className="bk-section" style={{ background: T.cream, padding: "72px 60px", fontFamily: "system-ui, sans-serif" }}>
      <style>{`
        @media (max-width: 1024px){
          .bk-section { padding: 40px 24px !important; }
          .bk-grid { grid-template-columns: 1fr 1fr !important; gap: 14px !important; }
          .bk-wrap { padding: 0 !important; }
        }
        @media (max-width: 520px){
          /* Match cadeau-banner Split mobile: 24px section-padding totaal,
              4:3 aspect, min-height 260px. Foto neemt volledige beschikbare
              breedte (viewport - 48px) — exact zelfde als de cadeau-foto. */
          .bk-section { padding: 32px 24px !important; }
          .bk-grid { grid-template-columns: 1fr !important; gap: 18px !important; }
          .bk-wrap { padding: 0 !important; }
          .bk-grid > a > div:first-child {
            aspect-ratio: 4 / 3 !important;
            min-height: 260px !important;
          }
        }
      `}</style>
      <div className="bk-wrap">
        <div style={{ marginBottom: 36 }}>
          <Header
            eyebrow={content.eyebrow}
            headline={content.headline}
            headlineItalic={content.headline_italic}
            subtitle={content.subtitle}
            align="center"
          />
        </div>
        <div
          className="bk-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 20,
            maxWidth: 1200,
            margin: "0 auto",
          }}
        >
          {styles.map((s) => (
            <StyleTile key={s.id} s={s} {...(content.city ? { city: content.city } : {})} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Variant 2 — Editorial mozaiek ────────────────────────────────────────
function MozaiekVariant({ content }: { content: BadkamersContent }) {
  const styles = resolveStyles(content.styles);
  const [hero, ...rest] = styles;
  return (
    <section style={{ background: T.creamSoft, padding: "80px 60px", fontFamily: "system-ui, sans-serif" }}>
      <style>{`
        @media (max-width: 1024px){
          .bk-moz-head { grid-template-columns: 1fr !important; gap: 20px !important; align-items: flex-start !important; }
          .bk-moz-grid { grid-template-columns: 1fr 1fr !important; }
          .bk-moz-hero { grid-row: auto !important; grid-column: 1 / -1 !important; }
          .bk-moz-wide { grid-column: 1 / -1 !important; }
          .bk-moz-wrap { padding: 40px 18px !important; }
        }
        @media (max-width: 520px){
          .bk-moz-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <div className="bk-moz-wrap">
        <div
          className="bk-moz-head"
          style={{
            marginBottom: 36,
          }}
        >
          <Header
            eyebrow={content.eyebrow}
            headline={content.headline ?? "Een"}
            headlineItalic={content.headline_italic ?? "visueel moodboard"}
            subtitle={content.subtitle ?? "Zes richtingen uit onze showroom — van strak en modern tot warm en klassiek."}
          />
        </div>
        <div
          className="bk-moz-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr",
            gridTemplateRows: "auto auto",
            gap: 16,
          }}
        >
          {hero ? (
            <div className="bk-moz-hero" style={{ gridRow: "1 / 3" }}>
              <a
                href="#"
                style={{
                  display: "block",
                  textDecoration: "none",
                  color: "inherit",
                  borderRadius: 16,
                  overflow: "hidden",
                  position: "relative",
                  height: "100%",
                }}
              >
                <div style={{ aspectRatio: "4 / 5", width: "100%", position: "relative", height: "100%" }}>
                  <StyleMedia style={hero} {...(content.city ? { city: content.city } : {})} />
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "linear-gradient(to top, rgba(28,28,26,0.7) 0%, rgba(28,28,26,0) 55%)",
                    }}
                  />
                  <div style={{ position: "absolute", bottom: 28, left: 28, right: 28 }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.14em",
                        color: "rgba(255,255,255,0.8)",
                        marginBottom: 6,
                        textTransform: "uppercase",
                      }}
                    >
                      Uitgelicht
                    </div>
                    <h3
                      style={{
                        fontFamily: "'Fraunces', serif",
                        fontSize: 44,
                        fontWeight: 500,
                        color: "#FFF",
                        margin: 0,
                        letterSpacing: "-0.015em",
                      }}
                    >
                      {hero.name}
                    </h3>
                  </div>
                </div>
              </a>
            </div>
          ) : null}
          {rest.slice(0, 4).map((s) => (
            <StyleTile key={s.id} s={s} aspect="4 / 3" size="sm" {...(content.city ? { city: content.city } : {})} />
          ))}
          {rest[4] ? (
            <div className="bk-moz-wide" style={{ gridColumn: "2 / 4" }}>
              <StyleTile s={rest[4]} aspect="16 / 6" size="sm" {...(content.city ? { city: content.city } : {})} />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

// ── Variant 3 — Premium dark galerij ─────────────────────────────────────
function DarkVariant({ content }: { content: BadkamersContent }) {
  const styles = resolveStyles(content.styles);
  return (
    <section style={{ background: T.dark, padding: "80px 60px", fontFamily: "system-ui, sans-serif" }}>
      <style>{`
        @media (max-width: 1024px){
          .bk-dark-grid { grid-template-columns: 1fr 1fr !important; gap: 14px !important; }
          .bk-dark-wrap { padding: 40px 14px !important; }
          .bk-dark-head { text-align: left !important; }
        }
        @media (max-width: 520px){
          .bk-dark-grid { grid-template-columns: 1fr !important; gap: 16px !important; }
          .bk-dark-wrap { padding: 36px 12px !important; }
        }
      `}</style>
      <div className="bk-dark-wrap">
        <div className="bk-dark-head" style={{ textAlign: "center", marginBottom: 40 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.2em",
              color: T.gold,
              marginBottom: 12,
              textTransform: "uppercase",
            }}
          >
            {content.eyebrow ?? "Collectie · zes sferen"}
          </div>
          <h2
            style={{
              fontFamily: "'Fraunces', serif",
              fontWeight: 500,
              fontSize: "clamp(30px, 4vw, 54px)",
              lineHeight: 1.02,
              color: "#F6F0DE",
              margin: "0 auto",
              letterSpacing: "-0.025em",
              maxWidth: 680,
            }}
          >
            {content.headline ? (
              <>
                {content.headline}
                {content.headline_italic ? (
                  <>
                    {" "}
                    <span style={{ fontStyle: "italic", fontWeight: 400, color: T.gold }}>{content.headline_italic}</span>
                  </>
                ) : null}
              </>
            ) : (
              <>
                Ontwerp uw <span style={{ fontStyle: "italic", fontWeight: 400, color: T.gold }}>droombadkamer</span>
              </>
            )}
          </h2>
        </div>
        <div
          className="bk-dark-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 20,
            maxWidth: 1200,
            margin: "0 auto",
          }}
        >
          {styles.map((s) => {
            const idx = styles.findIndex((x) => x.id === s.id);
            return (
              <a
                key={s.id}
                href="#"
                style={{
                  display: "block",
                  textDecoration: "none",
                  color: "inherit",
                  position: "relative",
                  borderRadius: 12,
                  overflow: "hidden",
                  aspectRatio: "3 / 4",
                  transition: "transform 0.3s",
                }}
              >
                <StyleMedia style={s} dark {...(content.city ? { city: content.city } : {})} />
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(to top, rgba(20,19,15,0.88) 0%, rgba(20,19,15,0.2) 50%, rgba(20,19,15,0) 70%)",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: 16,
                    left: 16,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    color: T.gold,
                    textTransform: "uppercase",
                  }}
                >
                  0{idx + 1}
                </div>
                <div style={{ position: "absolute", bottom: 22, left: 22, right: 22 }}>
                  <h3
                    style={{
                      fontFamily: "'Fraunces', serif",
                      fontSize: 28,
                      fontWeight: 500,
                      color: "#F6F0DE",
                      margin: 0,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {s.name}
                  </h3>
                  <div style={{ width: 40, height: 1, background: T.gold, marginTop: 10 }} />
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── Variant 4 — Bento / asymmetrisch ─────────────────────────────────────
function BentoVariant({ content }: { content: BadkamersContent }) {
  const styles = resolveStyles(content.styles);
  const ordered = styles;

  const bento: Array<{ col: string; row: string }> = [
    { col: "span 2", row: "span 2" },
    { col: "span 2", row: "span 1" },
    { col: "span 1", row: "span 1" },
    { col: "span 1", row: "span 2" },
    { col: "span 2", row: "span 1" },
    { col: "span 1", row: "span 1" },
  ];

  return (
    <section style={{ background: T.cream, padding: "80px 60px", fontFamily: "system-ui, sans-serif" }}>
      <style>{`
        @media (max-width: 1024px){
          .bk-bento { grid-template-columns: 1fr 1fr !important; grid-auto-rows: 150px !important; gap: 12px !important; }
          .bk-bento > a { grid-column: span 1 !important; grid-row: span 1 !important; }
          .bk-bento > a.bk-bento-hero { grid-column: span 2 !important; grid-row: span 2 !important; }
          .bk-bento-head { grid-template-columns: 1fr !important; gap: 20px !important; align-items: flex-start !important; }
          .bk-bento-wrap { padding: 40px 18px !important; }
        }
      `}</style>
      <div className="bk-bento-wrap">
        <div
          className="bk-bento-head"
          style={{
            marginBottom: 32,
          }}
        >
          <Header
            eyebrow={content.eyebrow}
            headline={content.headline ?? "Bladeren door"}
            headlineItalic={content.headline_italic ?? "elke sfeer"}
            subtitle={content.subtitle ?? "Zes verschillende richtingen — in de grootte die bij elke stijl past."}
          />
        </div>
        <div
          className="bk-bento"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gridAutoRows: "180px",
            gap: 14,
            maxWidth: 1200,
            margin: "0 auto",
          }}
        >
          {ordered.map((s, i) => {
            const span = bento[i] ?? { col: "span 1", row: "span 1" };
            const isBig = i === 0;
            const idx = styles.findIndex((x) => x.id === s.id);
            return (
              <a
                key={s.id}
                href="#"
                className={isBig ? "bk-bento-hero" : undefined}
                style={{
                  gridColumn: span.col,
                  gridRow: span.row,
                  display: "block",
                  textDecoration: "none",
                  color: "inherit",
                  position: "relative",
                  borderRadius: 14,
                  overflow: "hidden",
                  border: `1px solid ${T.line}`,
                  transition: "transform 0.25s",
                }}
              >
                <div style={{ position: "absolute", inset: 0 }}>
                  <StyleMedia style={s} {...(content.city ? { city: content.city } : {})} />
                </div>
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(to top, rgba(28,28,26,0.65) 0%, rgba(28,28,26,0) 55%)",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    bottom: isBig ? 24 : 16,
                    left: isBig ? 24 : 16,
                    right: 16,
                  }}
                >
                  <h3
                    style={{
                      fontFamily: "'Fraunces', serif",
                      fontSize: isBig ? 40 : 20,
                      fontWeight: 500,
                      color: "#FFF",
                      margin: 0,
                      letterSpacing: "-0.01em",
                      lineHeight: 1.05,
                    }}
                  >
                    {s.name}
                  </h3>
                </div>
                <div
                  style={{
                    position: "absolute",
                    top: 14,
                    right: 14,
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.92)",
                    color: T.ink,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  0{idx + 1}
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── Dispatcher ────────────────────────────────────────────────────────────
export function BadkamersBlock({ content, variant = "grid" }: Props) {
  switch (variant) {
    case "mozaiek":
      return <MozaiekVariant content={content} />;
    case "dark":
      return <DarkVariant content={content} />;
    case "bento":
      return <BentoVariant content={content} />;
    case "grid":
    default:
      return <GridVariant content={content} />;
  }
}

export default BadkamersBlock;
